/**
 * tokens — personalisation tokens for the message composer and email designer.
 * Approved UX: `Personalisation Tokens - UX Proposal.dc.html` (2026-07-31).
 *
 * The user always writes and sees NAMED tokens on every surface (operator
 * ruling). WhatsApp is the exception underneath, not above: Meta requires
 * numbered placeholders, so `compileForWhatsApp()` converts named → {{1}}, {{2}}
 * at submit time and hands back the mapping to display. One mental model for the
 * user; Meta still gets what it needs.
 *
 * Syntax: {{token}} or {{token | fallback}}. Legacy single-brace {token} rows
 * are recognised too, so nothing already written breaks.
 *
 * The catalog here is PROVISIONAL — REQ-115 asks for a real per-context read.
 * Marked as such in the UI so nobody mistakes it for a contract.
 */
import { useEffect, useRef, useState, type JSX } from "react";
import { alpha, Btn, Icon, T } from "@v0y4ge/jrni-ui";
import { useT } from "../../lib/i18n";

export type TokenType = "text" | "link" | "date" | "number";
export type TokenGroup = "person" | "appointment" | "event" | "queue" | "org";
export interface TokenDef {
  id: string; label: string; group: TokenGroup; type: TokenType;
  desc: string; sample: string;
  /** Can legitimately be empty for a real recipient → offer a fallback. */
  blankable?: boolean;
}

/* ui-config — provisional vocabulary (REQ-115), not tenant data. */
export const TOKENS: TokenDef[] = [
  { id: "first_name", label: "First name", group: "person", type: "text", desc: "Given name", sample: "Noor", blankable: true },
  { id: "last_name", label: "Last name", group: "person", type: "text", desc: "Family name", sample: "Haddad", blankable: true },
  { id: "full_name", label: "Full name", group: "person", type: "text", desc: "Both names", sample: "Noor Haddad", blankable: true },
  { id: "email", label: "Email", group: "person", type: "text", desc: "Their email address", sample: "n.haddad@example.com" },
  { id: "mobile", label: "Mobile", group: "person", type: "text", desc: "Their mobile number", sample: "+1 617 555 0148" },
  { id: "preferred_language", label: "Preferred language", group: "person", type: "text", desc: "Language on their profile", sample: "English", blankable: true },

  { id: "service", label: "Service", group: "appointment", type: "text", desc: "What they booked", sample: "Mortgage consultation" },
  { id: "staff", label: "Staff member", group: "appointment", type: "text", desc: "Who they are seeing", sample: "Jordan Pierce", blankable: true },
  { id: "date", label: "Date", group: "appointment", type: "date", desc: "Appointment date", sample: "Tuesday 14 October" },
  { id: "time", label: "Time", group: "appointment", type: "date", desc: "Appointment start time", sample: "14:30" },
  { id: "duration", label: "Duration", group: "appointment", type: "number", desc: "Length in minutes", sample: "45" },
  { id: "location", label: "Location", group: "appointment", type: "text", desc: "Branch or store name", sample: "Boston — Back Bay" },
  { id: "location_address", label: "Location address", group: "appointment", type: "text", desc: "Full postal address of the appointment", sample: "200 Boylston Street, Boston, MA 02116" },
  { id: "directions_url", label: "Directions link", group: "appointment", type: "link", desc: "Map link for the location", sample: "https://jrni.example/directions/ab-backbay" },
  { id: "manage_url", label: "Manage booking link", group: "appointment", type: "link", desc: "Reschedule or cancel", sample: "https://jrni.example/manage/8f2c" },
  { id: "join_url", label: "Video join link", group: "appointment", type: "link", desc: "For virtual appointments", sample: "https://jrni.example/join/8f2c", blankable: true },

  { id: "event_name", label: "Event name", group: "event", type: "text", desc: "Title of the event", sample: "Autumn Advice Evening" },
  { id: "event_date", label: "Event date", group: "event", type: "date", desc: "Date it runs", sample: "Wednesday 15 October" },
  { id: "event_time", label: "Event time", group: "event", type: "date", desc: "Start time", sample: "18:00" },
  { id: "venue", label: "Venue", group: "event", type: "text", desc: "Venue name", sample: "Back Bay Auditorium" },
  { id: "venue_address", label: "Venue address", group: "event", type: "text", desc: "Full postal address of the venue", sample: "200 Boylston Street, Boston, MA 02116" },
  { id: "ticket_url", label: "Ticket link", group: "event", type: "link", desc: "Their ticket", sample: "https://jrni.example/ticket/71ba" },
  { id: "register_url", label: "Registration link", group: "event", type: "link", desc: "Sign-up page", sample: "https://jrni.example/e/autumn-advice" },
  { id: "recap_url", label: "Recap link", group: "event", type: "link", desc: "Post-event summary", sample: "https://jrni.example/recap/71ba" },

  { id: "queue_name", label: "Queue name", group: "queue", type: "text", desc: "Which queue", sample: "Lobby walk-ins" },
  { id: "position", label: "Position", group: "queue", type: "number", desc: "Place in line", sample: "4" },
  { id: "est_wait", label: "Estimated wait", group: "queue", type: "number", desc: "Minutes remaining", sample: "12" },
  { id: "delay", label: "Delay", group: "queue", type: "number", desc: "Minutes behind schedule", sample: "20" },
  { id: "to_queue", label: "Moved-to queue", group: "queue", type: "text", desc: "Destination on a transfer", sample: "Notary desk" },
  { id: "rejoin_url", label: "Rejoin link", group: "queue", type: "link", desc: "Get back in line", sample: "https://jrni.example/q/rejoin/4a1" },

  /* Neutral sample, NOT a real tenant's name. This string is rendered — the
   * message composer prints `{{org_name}} = Organisation name · <sample>` and the
   * preview substitutes it — so a live tenant's operator was shown another
   * customer's organisation as the example. Matches the neutral convention the
   * rest of this list already uses (`jrni.example` hosts). // ui-config */
  { id: "org_name", label: "Organisation name", group: "org", type: "text", desc: "Your organisation", sample: "Example Organisation" },
  { id: "org_phone", label: "Organisation phone", group: "org", type: "text", desc: "Main contact number", sample: "+1 617 555 0100" },
  { id: "org_address", label: "Organisation address", group: "org", type: "text", desc: "Registered address", sample: "200 Boylston Street, Boston, MA 02116" },
  { id: "book_url", label: "Booking link", group: "org", type: "link", desc: "Start a new booking", sample: "https://jrni.example/book" },
  { id: "survey_url", label: "Feedback link", group: "org", type: "link", desc: "Survey or feedback form", sample: "https://jrni.example/feedback/8f2c" },
  { id: "unsubscribe_url", label: "Unsubscribe link", group: "org", type: "link", desc: "Required in marketing email footers", sample: "https://jrni.example/unsubscribe/8f2c" },
];

export const GROUP_LABEL: Record<TokenGroup, string> = {
  person: "Person", appointment: "Appointment", event: "Event", queue: "Queue", org: "Organisation",
};
/** Which resource a message is attached to — decides what can resolve. */
export type MsgContext = "appointment" | "event" | "queue" | "unknown";
/** person + org always resolve; the rest depend on what the message is attached to. */
export function tokensFor(ctx: MsgContext): TokenDef[] {
  return TOKENS.filter((tk) => tk.group === "person" || tk.group === "org" || tk.group === ctx);
}
export function unavailableFor(ctx: MsgContext): TokenDef[] {
  return TOKENS.filter((tk) => tk.group !== "person" && tk.group !== "org" && tk.group !== ctx);
}
/** Infer context from the message id/name — the seeded ids carry their family. */
export function contextOf(idOrName: string): MsgContext {
  const s = (idOrName || "").toLowerCase();
  if (s.indexOf("msg_q_") === 0 || s.indexOf("queue") >= 0) return "queue";
  if (s.indexOf("msg_b_") === 0 || s.indexOf("booking") >= 0 || s.indexOf("appointment") >= 0) return "appointment";
  if (s.indexOf("msg_") === 0 || s.indexOf("event") >= 0) return "event";
  return "unknown";
}

export const tokenDef = (id: string) => TOKENS.find((tk) => tk.id === id) || null;
/** {{tok}} / {{tok | fallback}} — and legacy {tok}. */
const TOKEN_RE = /\{\{\s*([a-z0-9_]+)\s*(?:\|\s*([^}]*?)\s*)?\}\}|\{\s*([a-z0-9_]+)\s*\}/gi;
export interface FoundToken { id: string; fallback?: string; raw: string; index: number; known: boolean }
export function findTokens(text: string): FoundToken[] {
  const out: FoundToken[] = [];
  const re = new RegExp(TOKEN_RE);
  let m: RegExpExecArray | null;
  while ((m = re.exec(text || "")) !== null) {
    const id = (m[1] || m[3] || "").toLowerCase();
    out.push({ id, fallback: m[2], raw: m[0], index: m.index, known: !!tokenDef(id) });
  }
  return out;
}
export const unknownTokens = (text: string) => findTokens(text).filter((f) => !f.known).map((f) => f.id);
export const hasTokens = (text: string) => findTokens(text).length > 0;
/** Render with sample values — what the preview shows. */
export function resolveSamples(text: string): string {
  return (text || "").replace(new RegExp(TOKEN_RE), (raw, a, fb, b) => {
    const d = tokenDef((a || b || "").toLowerCase());
    if (!d) return raw;
    return d.blankable && fb ? String(fb) : d.sample;
  });
}
/** SMS segments from the RESOLVED text — a token's real length is unknown until
 *  send, so the counter is presented as an estimate wherever tokens appear. */
export function smsSegments(text: string): { chars: number; segments: number; estimated: boolean } {
  const resolved = resolveSamples(text);
  return { chars: resolved.length, segments: Math.max(1, Math.ceil(resolved.length / 160)), estimated: hasTokens(text) };
}
/** Named → Meta's numbered placeholders, in first-appearance order. */
export function compileForWhatsApp(text: string): { body: string; map: { n: number; id: string; sample: string }[] } {
  const order: string[] = [];
  const body = (text || "").replace(new RegExp(TOKEN_RE), (raw, a, _fb, b) => {
    const id = (a || b || "").toLowerCase();
    if (!tokenDef(id)) return raw;
    let i = order.indexOf(id);
    if (i < 0) { order.push(id); i = order.length - 1; }
    return `{{${i + 1}}}`;
  });
  return { body, map: order.map((id, i) => ({ n: i + 1, id, sample: (tokenDef(id) || { sample: "" }).sample })) };
}

/* ── The field ─────────────────────────────────────────────────────────── */

const menuWrap: React.CSSProperties = { position: "absolute", zIndex: 40, minWidth: 288, maxHeight: 292, overflowY: "auto", background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, boxShadow: T.shadowLg, padding: 6 };

/** Shared token picker list. `filterType` narrows to one token type (link fields). */
function TokenMenu({ ctx, filterType, query, onPick, onClose, style }: {
  ctx: MsgContext; filterType?: TokenType; query: string;
  onPick: (tk: TokenDef, fallback?: string) => void; onClose: () => void; style?: React.CSSProperties;
}) {
  const t = useT();
  const [q, setQ] = useState(query);
  const [fbFor, setFbFor] = useState<TokenDef | null>(null);
  const [fbVal, setFbVal] = useState("");
  useEffect(() => setQ(query), [query]);
  const avail = tokensFor(ctx).filter((tk) => (!filterType || tk.type === filterType));
  const needle = q.trim().toLowerCase();
  const list = avail.filter((tk) => !needle || tk.id.indexOf(needle) >= 0 || tk.label.toLowerCase().indexOf(needle) >= 0);
  const groups: TokenGroup[] = ["person", "appointment", "event", "queue", "org"];
  const missing = unavailableFor(ctx).filter((tk) => !filterType || tk.type === filterType);
  return (
    <div style={{ ...menuWrap, ...(style || {}) }} onClick={(e) => e.stopPropagation()} data-testid="token-menu">
      {fbFor ? (
        <div style={{ padding: 8 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: T.n1 }}>{fbFor.label}</div>
          <div style={{ fontSize: 11.5, color: T.soft, margin: "4px 0 8px", lineHeight: 1.45 }}>{t("messaging.tokens.fallbackHelp", "Shown when this is empty for a recipient. Leave blank for no fallback.")}</div>
          <input autoFocus value={fbVal} onChange={(e) => setFbVal(e.target.value)} placeholder={t("messaging.tokens.fallbackPlaceholder", "e.g. there")}
            onKeyDown={(e) => { if (e.key === "Enter") { onPick(fbFor, fbVal.trim() || undefined); setFbFor(null); } if (e.key === "Escape") setFbFor(null); }}
            style={{ width: "100%", height: 32, padding: "0 9px", border: `1px solid ${T.n4}`, borderRadius: 7, fontSize: 13, fontFamily: T.font, boxSizing: "border-box" }} />
          <div style={{ display: "flex", gap: 6, marginTop: 8, justifyContent: "flex-end" }}>
            <Btn size="sm" variant="secondary" onClick={() => setFbFor(null)}>{t("common.actions.cancel", "Cancel")}</Btn>
            <Btn size="sm" onClick={() => { onPick(fbFor, fbVal.trim() || undefined); setFbFor(null); }}>{t("messaging.tokens.insert", "Insert")}</Btn>
          </div>
        </div>
      ) : (
        <>
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("messaging.tokens.search", "Search tokens…")}
            onKeyDown={(e) => { if (e.key === "Escape") onClose(); if (e.key === "Enter" && list.length) onPick(list[0]); }}
            style={{ width: "100%", height: 32, padding: "0 9px", border: `1px solid ${T.n4}`, borderRadius: 7, fontSize: 13, fontFamily: T.font, marginBottom: 6, boxSizing: "border-box" }} />
          {list.length === 0 ? <div style={{ padding: "10px 8px", fontSize: 12.5, color: T.soft }}>{t("messaging.tokens.none", "No token matches that.")}</div> : null}
          {groups.map((g) => {
            const rows = list.filter((tk) => tk.group === g);
            if (!rows.length) return null;
            return (
              <div key={g}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.04, textTransform: "uppercase", color: T.soft, padding: "8px 8px 4px" }}>{GROUP_LABEL[g]}</div>
                {rows.map((tk) => (
                  <button key={tk.id} type="button" data-testid="token-option"
                    onClick={() => (tk.blankable ? (setFbFor(tk), setFbVal("")) : onPick(tk))}
                    style={{ display: "block", width: "100%", textAlign: "left", padding: "7px 8px", border: "none", borderRadius: 7, background: "transparent", cursor: "pointer", fontFamily: T.font }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                      <span style={{ fontSize: 12.5, fontWeight: 600, color: T.n1 }}>{tk.label}</span>
                      {tk.blankable ? <span style={{ fontSize: 9.5, fontWeight: 700, color: T.orange1, background: T.orange2, borderRadius: 4, padding: "1px 4px" }}>{t("messaging.tokens.canBeBlank", "CAN BE BLANK")}</span> : null}
                    </div>
                    <div style={{ fontSize: 11, color: T.soft, marginTop: 1, fontFamily: T.fontMono }}>{`{{${tk.id}}}`}</div>
                  </button>
                ))}
              </div>
            );
          })}
          {missing.length && !needle ? (
            <div style={{ borderTop: `1px solid ${T.border}`, marginTop: 6, padding: "8px 8px 4px" }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.04, textTransform: "uppercase", color: T.n3 }}>{t("messaging.tokens.unavailable", "Not available here")}</div>
              <div style={{ fontSize: 11, color: T.n3, lineHeight: 1.5, marginTop: 4 }}>{missing.slice(0, 6).map((tk) => tk.label).join(" · ")}{missing.length > 6 ? " …" : ""}</div>
              <div style={{ fontSize: 11, color: T.soft, marginTop: 5, lineHeight: 1.45 }}>{t("messaging.tokens.unavailableWhy", "These would resolve to nothing for this message, so they are not offered.")}</div>
            </div>
          ) : null}
          <div style={{ borderTop: `1px solid ${T.border}`, marginTop: 6, padding: "7px 8px 3px", fontSize: 10.5, color: T.n3, lineHeight: 1.4 }}>{t("messaging.tokens.provisional", "Provisional list — the sendable set comes from the backend (REQ-115).")}</div>
        </>
      )}
    </div>
  );
}

/**
 * A text input/textarea with all three approved insertion affordances:
 * type `{{` for autocomplete at the caret, an Insert-token button, and optional
 * quick chips. Value stays plain text — no chip editor (see the proposal).
 */
export function TokenField({ value, onChange, ctx, multiline, rows, maxLength, placeholder, ariaRequired, invalid, style, quick, filterType, testid }: {
  value: string; onChange: (v: string) => void; ctx: MsgContext;
  multiline?: boolean; rows?: number; maxLength?: number; placeholder?: string;
  ariaRequired?: boolean; invalid?: boolean; style?: React.CSSProperties;
  /** Token ids offered as one-click chips under the field. */
  quick?: string[]; filterType?: TokenType; testid?: string;
}) {
  const t = useT();
  const ref = useRef<HTMLTextAreaElement & HTMLInputElement>(null);
  const [menu, setMenu] = useState<null | { query: string; fromCaret: boolean }>(null);
  const unknown = unknownTokens(value);

  const insertAt = (text: string) => {
    const el = ref.current;
    const start = el && typeof el.selectionStart === "number" ? el.selectionStart : value.length;
    const end = el && typeof el.selectionEnd === "number" ? el.selectionEnd : value.length;
    const next = value.slice(0, start) + text + value.slice(end);
    onChange(next);
    requestAnimationFrame(() => { if (el) { el.focus(); const p = start + text.length; try { el.setSelectionRange(p, p); } catch { /* input types without selection */ } } });
  };
  /** Insertion from the caret path replaces the trigger the user typed. */
  const commit = (tk: TokenDef, fallback?: string) => {
    const tok = `{{${tk.id}${fallback ? ` | ${fallback}` : ""}}}`;
    const el = ref.current;
    if (menu?.fromCaret && el && typeof el.selectionStart === "number") {
      const caret = el.selectionStart;
      const before = value.slice(0, caret);
      const trig = before.lastIndexOf("{{");
      if (trig >= 0) {
        const next = value.slice(0, trig) + tok + value.slice(caret);
        onChange(next);
        setMenu(null);
        requestAnimationFrame(() => { if (el) { el.focus(); const p = trig + tok.length; try { el.setSelectionRange(p, p); } catch { /* noop */ } } });
        return;
      }
    }
    insertAt(tok);
    setMenu(null);
  };
  /** Typing `{{` opens the menu; whatever follows filters it. */
  const onInput = (v: string) => {
    onChange(v);
    const el = ref.current;
    const caret = el && typeof el.selectionStart === "number" ? el.selectionStart : v.length;
    const before = v.slice(0, caret);
    const trig = before.lastIndexOf("{{");
    if (trig >= 0 && before.indexOf("}}", trig) < 0) {
      const q = before.slice(trig + 2);
      if (q.length <= 24 && !/[{}\n]/.test(q)) { setMenu({ query: q, fromCaret: true }); return; }
    }
    if (menu?.fromCaret) setMenu(null);
  };

  const common = {
    ref: ref as never, value, placeholder,
    "aria-required": ariaRequired ? ("true" as const) : undefined,
    "aria-invalid": invalid ? ("true" as const) : undefined,
    "data-testid": testid,
    onChange: (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => onInput(e.target.value),
    onKeyDown: (e: React.KeyboardEvent) => { if (e.key === "Escape" && menu) { e.stopPropagation(); setMenu(null); } },
    style: { width: "100%", padding: multiline ? 12 : "0 12px", height: multiline ? "auto" : 40, border: `1px solid ${invalid ? T.red1 : T.n4}`, borderRadius: 8, background: T.white, color: T.n1, fontSize: 14, fontFamily: T.font, outline: "none", boxSizing: "border-box" as const, lineHeight: 1.5, resize: multiline ? ("vertical" as const) : undefined, ...(style || {}) },
  };

  return (
    <div style={{ position: "relative" }} onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setMenu(null); }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <span style={{ flex: 1 }} />
        <button type="button" onClick={() => setMenu(menu && !menu.fromCaret ? null : { query: "", fromCaret: false })} data-testid="token-insert-btn"
          style={{ display: "inline-flex", alignItems: "center", gap: 5, minHeight: 26, padding: "0 9px", borderRadius: 7, border: `1px solid ${T.border}`, background: T.white, color: T.p1, fontFamily: T.font, fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}>
          <Icon.plus size={11} />{t("messaging.tokens.insertToken", "Insert token")}
        </button>
      </div>
      {multiline
        ? <textarea {...common} rows={rows || 6} maxLength={maxLength} />
        : <input {...common} maxLength={maxLength} />}
      {menu ? (
        <TokenMenu ctx={ctx} filterType={filterType} query={menu.query} onPick={commit} onClose={() => setMenu(null)} style={{ top: multiline ? 44 : 40, left: 0 }} />
      ) : null}
      {quick && quick.length ? (
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 7 }}>
          {quick.map((id) => {
            const tk = tokenDef(id);
            if (!tk) return null;
            return (
              <button key={id} type="button" onClick={() => insertAt(`{{${tk.id}}}`)} data-testid="token-chip"
                style={{ minHeight: 24, padding: "0 9px", borderRadius: 999, border: `1px solid ${T.p3}`, background: T.p4, color: T.p1, fontFamily: T.font, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>{tk.label}</button>
            );
          })}
        </div>
      ) : null}
      {unknown.length ? (
        <div role="alert" style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11.5, color: T.red1, marginTop: 6 }}>
          <Icon.warn size={12} />{t("messaging.tokens.unknown", "No such token:")} {unknown.slice(0, 3).map((u) => `{{${u}}}`).join(", ")}
        </div>
      ) : null}
    </div>
  );
}

/** Small inline picker for fields that are not TokenFields (e.g. block URLs). */
export function TokenButton({ ctx, filterType, onPick, label }: { ctx: MsgContext; filterType?: TokenType; onPick: (tokenText: string) => void; label?: string }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: "relative" }} onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setOpen(false); }}>
      <button type="button" onClick={() => setOpen(!open)} data-testid="token-insert-btn"
        style={{ display: "inline-flex", alignItems: "center", gap: 5, minHeight: 26, padding: "0 9px", borderRadius: 7, border: `1px solid ${T.border}`, background: T.white, color: T.p1, fontFamily: T.font, fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}>
        <Icon.plus size={11} />{label || t("messaging.tokens.insertToken", "Insert token")}
      </button>
      {open ? <TokenMenu ctx={ctx} filterType={filterType} query="" onClose={() => setOpen(false)} style={{ top: 30, right: 0 }}
        onPick={(tk, fb) => { onPick(`{{${tk.id}${fb ? ` | ${fb}` : ""}}}`); setOpen(false); }} /> : null}
    </div>
  );
}

/** Sample-resolved preview text + the toggle label, shared by both previews. */
export function usePreviewTokens(): { showRaw: boolean; toggle: () => void; render: (s: string) => string; Toggle: () => JSX.Element } {
  const t = useT();
  const [showRaw, setShowRaw] = useState(false);
  const toggle = () => setShowRaw((v) => !v);
  const Toggle = () => (
    <button type="button" onClick={toggle} data-testid="token-preview-toggle"
      style={{ minHeight: 24, padding: "0 9px", borderRadius: 999, border: `1px solid ${showRaw ? T.n1 : T.border}`, background: showRaw ? T.n1 : T.white, color: showRaw ? T.white : T.n2, fontFamily: T.font, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
      {showRaw ? t("messaging.tokens.showValues", "Show values") : t("messaging.tokens.showTokens", "Show tokens")}
    </button>
  );
  return { showRaw, toggle, render: (s: string) => (showRaw ? s : resolveSamples(s)), Toggle };
}
